import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
const FAQ_BASE_URL =
  "https://hiring-assistant-assets.s3.ap-south-1.amazonaws.com/qualifyme-console-faq/";

dayjs.extend(relativeTime);

export const convertDateToTime = (data: Date): string => {
  const date = new Date(data);
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  const timeString = date.toLocaleTimeString("en-US", options);
  return timeString;
};

export const formatDate = (data: Date): string => {
  const date = new Date(data);

  const dayOfWeek = date.toLocaleDateString("en-US", { weekday: "long" });
  const day = date.getUTCDate();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getUTCMonth()];

  const getOrdinalSuffix = (n: number): string => {
    if (n > 3 && n < 21) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const dayWithSuffix = day + getOrdinalSuffix(day);

  return `${dayOfWeek} ${dayWithSuffix} ${month}`;
};

export const getRemainingDays = (endDate: any): number => {
  const targetDate = new Date(endDate);
  const today = new Date();

  const targetDateOnly = new Date(
    targetDate.getFullYear(),
    targetDate.getMonth(),
    targetDate.getDate()
  );
  const todayDateOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const diffTime = targetDateOnly.getTime() - todayDateOnly.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

export const convertTime = (timestamp: string): string => {
  return dayjs(timestamp).fromNow();
};

export const formatTime = (dateString: any) => {
  const date = new Date(dateString);
  let hours = date.getHours();
  const minutes = date.getMinutes();

  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours || 12;

  const formattedMinutes = minutes < 10 ? "0" + minutes : minutes;

  return `${hours}:${formattedMinutes} ${ampm}`;
};

export const getNextDueDate = (data: any) => {
  const lastDate = new Date(data);
  const dueDate = new Date(lastDate.setDate(lastDate.getDate() + 30));
  return dueDate.toDateString();
};

export const getNextPaymentRemainingDays = (data: any) => {
  const lastDate = new Date(data);
  const dueDate = new Date(lastDate.setDate(lastDate.getDate() + 30));
  const remainingDays = getRemainingDays(dueDate);
  return remainingDays;
};

export const formatDateToString = (date: Date | string) => {
  try {
    const parsedDate = dayjs(date);

    if (!parsedDate.isValid()) {
      console.error(":rocket: ~ formatDateToString ~ invalid date");
      return "Invalid Date";
    }

    const result = parsedDate.format("DD/MM/YYYY");
    return result;
  } catch (error) {
    console.error(":rocket: ~ formatDateToString ~ error:", error);
    throw new Error(
      "Failed to format date: " +
        (error instanceof Error ? error.message : String(error))
    );
  }
};

export const parseJobPostDates = (jobPost: any): any => {
  if (!jobPost) return jobPost;

  // Create a copy to avoid mutating the original object
  const parsedJobPost = { ...jobPost };

  if (parsedJobPost.updatedAt) {
    parsedJobPost.updatedAt = new Date(parsedJobPost.updatedAt);
  }

  if (parsedJobPost.createdAt) {
    parsedJobPost.createdAt = new Date(parsedJobPost.createdAt);
  }

  return parsedJobPost;
};

export const generateTrendData = (value: number, date: string) => {
  if (value === 0) {
    return [
      { day: 1, value: 0 },
      { day: 2, value: 0 },
      { day: 3, value: 0 },
      { day: 4, value: 0 },
      { day: 5, value: 0 },
      { day: 6, value: 0 },
      { day: 7, value: 0 },
    ];
  }

  if (!date) {
    return null;
  }

  const postedDate = new Date(date);
  const currentDate = new Date();

  if (isNaN(postedDate.getTime())) {
    return null;
  }

  const daysSincePosted = Math.floor(
    (currentDate.getTime() - postedDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (daysSincePosted < 1) {
    return null;
  }

  const dataPoints = Math.min(7, Math.max(1, daysSincePosted));

  const data = [];
  const baseValue = Math.max(1, Math.floor(value * 0.1));

  for (let i = 0; i < dataPoints; i++) {
    const dayProgress = i / (dataPoints - 1);
    const expectedValue = Math.floor(
      baseValue + (value - baseValue) * dayProgress
    );

    const variation = 0.2;
    const minValue = Math.max(0, expectedValue * (1 - variation));
    const maxValue = expectedValue * (1 + variation);
    const actualValue = Math.floor(
      minValue + Math.random() * (maxValue - minValue)
    );

    data.push({
      day: i + 1,
      value: actualValue,
    });
  }

  return data;
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const generateSecureState = (): string => {
  const array = new Uint32Array(2);
  crypto.getRandomValues(array);
  return array.join("");
};

export function safeString(value: any): string {
  if (value === null || value === undefined) return "Not available";
  if (typeof value === "string") return value;
  if (typeof value === "number") return value.toString();
  if (typeof value === "boolean") return value.toString();
  return "Invalid data";
}

export function safeNumber(value: any): number {
  if (typeof value === "number") return value;
  if (typeof value === "string" && !isNaN(Number(value))) return Number(value);
  return 0;
}

export const formatTitleForUrl = (title: string): string => {
  const formattedTitle = title
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/--+/g, "-"); // Replace multiple hyphens with a single one

  return capitalizeFirstLetter(formattedTitle);
};

export const generateFaqLink = (
  title?: string | null,
  ext?: string | undefined
): string => {
  if (!title) return ""; // return empty string if title is null/undefined
  const formattedTitle = formatTitleForUrl(title);
  const fileExtension = ext || "html"; // Default to "html" when ext is undefined
  return `${FAQ_BASE_URL}${formattedTitle}.${fileExtension}`;
};

export const getInitials = (firstName?: string, lastName?: string): string => {
  const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : "";
  const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : "";
  return firstInitial + lastInitial;
};

export const isValidObjectId = (id: string): boolean => {
  return /^[0-9a-fA-F]{24}$/.test(id);
};

export const isAnnualMode = (startDate: Date, endDate: Date): boolean => {
  return dayjs(endDate).diff(dayjs(startDate), "month") >= 12;
};
